import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AppStoreButton from "./AppStoreButton";
import GooglePlayButton from "./GooglePlayButton";

const logoPathWhite = "/240822-Flateroo-Logo-white-rgb.svg";
const logoPathDark = "/240822-Flateroo-Logo-black-rgb.svg";

interface HeaderProps {
  changeLanguage?: (lng: string) => void;
  darkText?: boolean;
}

function Header({ changeLanguage, darkText = false }: HeaderProps) {
  const logoPath = darkText ? logoPathDark : logoPathWhite;
  const { i18n } = useTranslation();
  const current = (i18n.language || "en").slice(0, 2);
  const [isScrolled, setIsScrolled] = useState(false);

  const setLang = (lng: string) => {
    if (current === lng) return;
    i18n.changeLanguage(lng);
    if (typeof changeLanguage === "function") changeLanguage(lng);
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300
        ${
          isScrolled
            ? "backdrop-blur-md bg-black/20"
            : "backdrop-blur-0 bg-transparent"
        }`}
      style={{
        // Respect iOS notch
        paddingTop: "calc(1rem + env(safe-area-inset-top))",
      }}
    >
      <div className="px-4 md:px-6 pb-3 md:pb-4">
        {/* 3-column grid prevents overlap on small screens */}
        <div className="grid grid-cols-3 items-center">
          {/* LEFT: Store links */}
          <div className="flex items-center gap-3">
            {/* Compact icons on mobile */}
            <a
              href="https://apps.apple.com/app/flateroo/id6683311358"
              target="_blank"
              rel="noopener noreferrer"
              className="md:hidden block"
            >
              <img
                src={`${process.env.PUBLIC_URL}/app-store-icon.svg`}
                alt="App Store"
                className={`h-4 w-4 ${darkText ? "invert" : ""}`}
              />
            </a>
            <a
              href="#"
              onClick={(e) => e.preventDefault()}
              className="md:hidden block opacity-70"
              aria-disabled
              title="Coming soon on Google Play"
            >
              <img
                src={`${process.env.PUBLIC_URL}/google-play-icon.svg`}
                alt="Google Play (coming soon)"
                className="h-4 w-4"
              />
            </a>

            {/* Full badges on md+ */}
            <div className="hidden md:flex items-center gap-3">
              <AppStoreButton darkText={darkText} />
              <GooglePlayButton darkText={darkText} />
            </div>
          </div>

          {/* CENTER: Logo (auto-centered by grid) */}
          <div className="flex justify-center">
            <a href="/" aria-label="Go home">
              <img
                src={logoPath}
                alt="Flateroo logo"
                className="h-8 md:h-10 w-auto dark:invert cursor-pointer"
              />
            </a>
          </div>

          {/* RIGHT: Language switch */}
          <div className="flex justify-end">
            {/* Compact pill on mobile */}
            <div
              className={`flex md:hidden items-center gap-2 rounded-full backdrop-blur px-1 py-1 ${
                darkText ? "bg-gray-900/10" : "bg-gray-100/20"
              }`}
            >
              <button
                onClick={() => setLang("en")}
                aria-current={current === "en" ? "page" : undefined}
                className={`text-xs px-2 py-0.5 rounded-full ${
                  current === "en"
                    ? darkText
                      ? "bg-black text-white"
                      : "bg-white text-black"
                    : darkText
                    ? "text-black/70"
                    : "text-white/80"
                }`}
              >
                EN
              </button>
              <button
                onClick={() => setLang("de")}
                aria-current={current === "de" ? "page" : undefined}
                className={`text-xs px-2 py-0.5 rounded-full ${
                  current === "de"
                    ? darkText
                      ? "bg-black text-white"
                      : "bg-white text-black"
                    : darkText
                    ? "text-black/70"
                    : "text-white/80"
                }`}
              >
                DE
              </button>
            </div>

            {/* Full pill on md+ */}
            <nav
              aria-label="Language"
              className={`hidden md:flex items-center rounded-full backdrop-blur px-1 py-1 ${
                darkText ? "bg-gray-900/10" : "bg-gray-100/20"
              }`}
            >
              <button
                onClick={() => setLang("en")}
                aria-current={current === "en" ? "page" : undefined}
                className={`px-3 py-1 text-sm rounded-full transition ${
                  current === "en"
                    ? darkText
                      ? "bg-black text-white"
                      : "bg-white text-black"
                    : darkText
                    ? "text-black/70 hover:text-black"
                    : "text-white/80 hover:text-white"
                }`}
              >
                English
              </button>
              <button
                onClick={() => setLang("de")}
                aria-current={current === "de" ? "page" : undefined}
                className={`px-3 py-1 text-sm rounded-full transition ${
                  current === "de"
                    ? darkText
                      ? "bg-black text-white"
                      : "bg-white text-black"
                    : darkText
                    ? "text-black/70 hover:text-black"
                    : "text-white/80 hover:text-white"
                }`}
              >
                Deutsch
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Header;
